import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVault } from '../contexts/VaultContext';

interface ProjectFile {
  id: string;
  file_name: string;
  stored_name: string;
  file_size: number;
  file_type: string;
  upload_date: string;
  category?: string;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  uploading: boolean;
  completed: boolean;
}

const ProjectDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getProject, addFilesToProject, removeFileFromProject } = useVault();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  
  const project = getProject(projectId || '');
  
  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Project not found</h1>
          <button
            onClick={() => navigate('/vault')}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Vault
          </button>
        </div>
      </div>
    );
  }
  
  const files = project.files;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleFileUpload = async (uploadedFiles: FileList | null) => {
    if (!uploadedFiles || !projectId) return;

    try {
      // Convert FileList to File array
      const filesArray = Array.from(uploadedFiles);
      
      // Initialize progress tracking for each file
      const initialProgress = filesArray.map(file => ({
        fileName: file.name,
        progress: 0,
        uploading: true,
        completed: false
      }));
      setUploadProgress(initialProgress);
      
      // Define progress callback
      const onProgress = (fileName: string, progress: number) => {
        setUploadProgress(prev => prev.map(item => {
          if (item.fileName === fileName) {
            if (progress === 100) {
              // Mark as completed and schedule removal
              setTimeout(() => {
                setUploadProgress(current => current.filter(p => p.fileName !== fileName));
              }, 2000); // Remove after 2 seconds
              return {
                ...item,
                progress: 100,
                uploading: false,
                completed: true
              };
            } else if (progress === -1) {
              // Error case
              setTimeout(() => {
                setUploadProgress(current => current.filter(p => p.fileName !== fileName));
              }, 5000); // Remove after 5 seconds for errors
              return {
                ...item,
                progress: 0,
                uploading: false,
                completed: false
              };
            } else {
              return {
                ...item,
                progress,
                uploading: true,
                completed: false
              };
            }
          }
          return item;
        }));
      };
      
      await addFilesToProject(projectId, filesArray, onProgress);
    } catch (error) {
      console.error('Error uploading files:', error);
      // Clear progress on error
      setUploadProgress([]);
      alert(`Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getFileCategory = (type: string) => {
    if (type.includes('pdf')) return 'PDF Document';
    if (type.includes('word') || type.includes('docx')) return 'Word Document';
    if (type.includes('image')) return 'Image';
    if (type.includes('spreadsheet') || type.includes('excel')) return 'Spreadsheet';
    return 'Document';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDeleteFile = async (fileId: string) => {
    if (projectId) {
      try {
        await removeFileFromProject(projectId, fileId);
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    }
  };

  const filteredFiles = files.filter(file =>
    file.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSize = files.reduce((sum, file) => sum + file.file_size, 0);

  return (
    <div className="flex h-screen bg-white">
      {/* Custom Vault Sidebar */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col h-screen">
        {/* LexaLabs Title */}
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={() => navigate('/')}
            className="w-full text-left hover:bg-gray-100 rounded-lg p-2 transition-colors duration-150"
          >
            <span className="text-lg font-bold text-gray-800">LexaLabs</span>
          </button>
        </div>

        {/* Navigation */}
        <div className="p-3">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-150 mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
            Lexa Assistant
          </button>

          {/* Separator */}
          <div className="border-t border-gray-300 mb-3"></div>

          {/* Product Sections */}
          <div className="space-y-1">
            <button
              onClick={() => navigate('/vault')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium bg-gray-200 text-gray-900 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
              Vault
            </button>

            <button
              onClick={() => navigate('/regulations')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-150"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Regulations
            </button>

            <button
              onClick={() => navigate('/compliance-playbooks')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-150"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              Playbooks
            </button>
          </div>
        </div>

        <div className="flex-1"></div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Breadcrumb */}
        <div className="px-8 pt-6 pb-1">
          <p className="text-sm text-gray-500">
            <button onClick={() => navigate('/vault')} className="hover:text-gray-700">
              Vault
            </button>
            {' / '}
          </p>
        </div>

        {/* Header */}
        <div className="px-8 pb-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{project.name}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {files.length} files · {formatFileSize(totalSize)}
              </p>
            </div>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
              Share
            </button>
          </div>
        </div>

        {/* Upload Progress */}
        {uploadProgress.length > 0 && (
          <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Uploading Files</h3>
            <div className="space-y-3">
              {uploadProgress.map((progress) => (
                <div key={progress.fileName} className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate" title={progress.fileName}>
                        {progress.fileName}
                      </span>
                      <span className="text-sm text-gray-500">
                        {progress.completed ? (
                          <span className="text-green-600 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Complete
                          </span>
                        ) : progress.uploading ? (
                          `${progress.progress}%`
                        ) : (
                          <span className="text-red-600">Error</span>
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          progress.completed 
                            ? 'bg-green-500' 
                            : progress.uploading 
                              ? 'bg-blue-500' 
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${progress.completed ? 100 : progress.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {files.length === 0 ? (
            /* Empty State with Upload */
            <div className="flex flex-col items-center justify-center h-full">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`w-full max-w-md border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  isDragging 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Upload your files</h3>
                <p className="text-gray-500 mb-6">Drag and drop files here, or click to browse</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                >
                  Choose Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.gif,.txt,.csv,.xlsx,.pptx"
                />
              </div>
            </div>
          ) : (
            /* Files Table */
            <div>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6">
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors"
                  >
                    Upload Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.gif,.txt,.csv,.xlsx,.pptx"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last modified
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFiles.map((file) => (
                      <tr key={file.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {file.file_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            {file.category || getFileCategory(file.file_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {file.file_type.split('/')[1]?.toUpperCase() || 'FILE'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(file.upload_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(file.file_size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;